import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import FallbackSpinner from "../../../materio/@core/components/spinner";
import { client } from "../../../utils/axios-client";
import CustomChip from 'src/materio/@core/components/mui/chip'
import { cloneDeep } from "lodash";
import { ApiResponseType } from "../../../types";
import ApplicationStore from "../../../stores/ApplicationStore";
import PageHeader from "../../../materio/@core/components/page-header";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import EditIcon from '@mui/icons-material/Edit';
import EditCustomerDetails from "./EditCustomerDetails";
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from "../../../hooks/useAuth";
import ConfirmationDialog from "../../common/ConfirmationDialog";
import { v4 as uuid } from 'uuid';
import RenderIf, { ClaimType, ClaimValue } from "../../common/RenderIf";
import { useParams } from "react-router-dom";
import { SelectOption } from "../../../types/common";
import { customersOptionsApiEndpoint } from "../../../configs/endpoints";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: 2,
    verticalAlign: 'top'
}));

export type CustomerDetail = {
    id: string,
    linkedUserId: string,
    customerId: string,
    customerIdError: boolean,
    customerName: string,
    emailAddress: string,
    emailAddressError: boolean,
    emailUserAccount: string,
    emailUserAccountError: boolean,
    smtpOutgoingHost: string,
    smtpOutgoingHostError: boolean,
    smtpOutgoingPort: string,
    smtpOutgoingPortError: boolean,
    emailType: string,
    smtpPassword: string,
    smtpPasswordError: boolean,
    kpimi: number,
    kpimiError: boolean,
    kpimql: number,
    kpimqlError: boolean,
    kpidc: number,
    kpidcError: boolean,
    kpiOpportunity: number,
    kpiOpportunityError: boolean,
    kpiActivities: number,
    kpiActivitiesError: boolean,
    emailSignature: string,
    emailSignatureError: boolean,
    outgoingVoiceMessageUrl: string,
    [key: string]: any
}

const CustomerDetailsTable = () => {
    const [data, setData] = useState<CustomerDetail[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const { user, setUser } = useAuth();
    const [selectedRecord, setSelectedRecord] = useState(null as null | CustomerDetail);
    const [editRecordOpen, setEditRecordOpen] = useState(false);
    let { userId } = useParams();
    const [customers, setCustomers] = useState([] as SelectOption[])

    //useEffect(() => {
    //    if (data) {
    //        setSelectedActivityType(data[0]);
    //        setOutcomeListOpen(true);
    //    }
    //}, [data])

    useEffect(() => {
        loadRecords()
    }, [])

    const loadRecords = async () => {
        //setIsLoading(true);
        const response = await client.get(`/api/admin/users/customer-details`, { params: { userId: userId } });
        setData(response.data);

        var response4 = await client.get(customersOptionsApiEndpoint);
        setCustomers(response4.data);

        setIsLoading(false);
    }


    if (isLoading) return <FallbackSpinner />;

    if (!data) return <Box>Error fetching activity types</Box>;

    const handleUpdateCurrentCustomer = async (updatedValue: any) => {
        const response = await client.post<ApiResponseType>('/api/admin/users/update-current-customer', {
            userId: userId,
            customerId: updatedValue ? updatedValue : null
        });

        if (response.data.succeeded) {
            setUser({ ...user!, currentCustomerId: updatedValue })
            ApplicationStore.update(s => {
                s.snackbarMessage = "Settings Saved Successfully";
                s.snackbarOpen = true;
                s.snackbarType = 'success'
            })
        }
        else {
            ApplicationStore.update(s => {
                s.snackbarMessage = "Error saving settings";
                s.snackbarOpen = true;    
                s.snackbarType = 'error'
            })
        }
    }


    const onEdit = (row: CustomerDetail) => {
        setSelectedRecord(row);
        setEditRecordOpen(true);
    }

    const onAdd = () => {
        setSelectedRecord({
            id: uuid(),
            linkedUserId: userId,
            emailType: "NONE"
        } as CustomerDetail);

        setEditRecordOpen(true);
    }

    const onDelete = async (row: CustomerDetail) => {
        const result = confirm("Are you sure you want to delete these settings?");

        if (result) {
            const response = await client.delete<ApiResponseType>(`/api/admin/users/customer-details`, { params: { id: row.id } });

            if (response.data.succeeded) {
                ApplicationStore.update(s => {
                    s.snackbarMessage = "Customer Deleted Successfully";
                    s.snackbarOpen = true;
                })

                loadRecords();
            } else {
                alert('error saving settings');
            }
        }


        ///setSelectedRecord(row);
        ///setEditRecordOpen(true);
    }

    const onCloseEditRecord = () => {
        loadRecords();
        setSelectedRecord(null);
        setEditRecordOpen(false);
    }

    const onUpdateInt = async (index: number, prop: string, value: string) => {
        const temp = cloneDeep(data);
        temp[index][prop] = parseInt(value);
        setData(temp);
    }

    const onUpdateString = async (index: number, prop: string, value: string) => {
        const temp = cloneDeep(data);
        temp[index][prop] = value;
        setData(temp);
    }

    const onUpdateBoolean = async (index: number, prop: string, value: boolean) => {
        const temp = cloneDeep(data);
        temp[index][prop] = value;
        setData(temp);
    }

    const onSaveDialog = async () => {
        const response = await client.post<ApiResponseType>('/api/activity-types', { items: data });
        if (response.data.succeeded) {
            loadRecords();

            ApplicationStore.update(s => {
                s.snackbarMessage = "Activity Types Saved Successfully";
                s.snackbarOpen = true;
            })
        } else {
            alert('error updating activity types');
        }
    }

    return <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '40vh', maxHeight: '40vh', rowGap: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4, alignItems: 'center' }}>
                <FormControl size="small" sx={{ width: 'auto' }}>
                    <InputLabel id="demo-simple-select-label">Current Customer</InputLabel>
                    <Select
                        value={user?.currentCustomerId}
                        onChange={(e) => handleUpdateCurrentCustomer(e.target.value)}
                        label="Current Customer"
                    >
                        <MenuItem value=''>None</MenuItem>
                        {customers.map(item => <MenuItem value={item.value}>{item.name}</MenuItem>)}
                    </Select>
                </FormControl>
                <RenderIf claimType={ClaimType.UserProfile} claimValue={ClaimValue.AddUserProfileCustomer}>
                    <Button variant="contained" size="small" onClick={onAdd} endIcon={<AddIcon />}>Add</Button>
                </RenderIf>
            </Box>
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell colSpan={5}></StyledTableCell>
                            <StyledTableCell colSpan={4} sx={{ textAlign: 'center' }}>Monthly KPIs</StyledTableCell>
                            <StyledTableCell sx={{ width: 100 }}></StyledTableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell >Customer Name</StyledTableCell>
                            <StyledTableCell sx={{ width: 120, textAlign: 'center' }}>Type</StyledTableCell>
                            <StyledTableCell>Email Account</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>SMTP Host</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>SMTP Port</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>Activities</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>MI</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>MQL</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>DC</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>OPPORTUNITIES</StyledTableCell>
                            <StyledTableCell sx={{ width: 100 }}></StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, index) => <TableRow>
                            <StyledTableCell>{item.customerName}</StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.emailType}
                            </StyledTableCell>
                            <StyledTableCell>
                                {item.emailUserAccount}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.smtpOutgoingHost}
                            </StyledTableCell>
                            <StyledTableCell>
                                {item.smtpOutgoingPort}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.kpiActivities}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.kpimi}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.kpimql}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.kpidc}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: 100, textAlign: 'center' }}>
                                {item.kpiOpportunity}
                            </StyledTableCell>

                            <StyledTableCell title="Edit">
                                <RenderIf claimType={ClaimType.UserProfile} claimValue={ClaimValue.DeleteUserProfileCustomer}>
                                    <IconButton size="small" onClick={() => onDelete(item)}><DeleteIcon /></IconButton>
                                </RenderIf>
                                <RenderIf claimType={ClaimType.UserProfile} claimValue={ClaimValue.EditUserProfileCustomer}>
                                    <IconButton size="small" onClick={() => onEdit(item)}><EditIcon /></IconButton>
                                </RenderIf>

                            </StyledTableCell>
                        </TableRow>

                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
        {selectedRecord && <EditCustomerDetails open={editRecordOpen} onClose={onCloseEditRecord} record={selectedRecord} selectedCustomerIds={data.map(item => item.customerId)} />}
    </Box>
}

export default CustomerDetailsTable;